import { useCallback, useEffect, useState } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { urlBase64ToUint8Array, subscriptionToRow } from '../lib/pushHelpers'

const TABLE = 'push_subscriptions'
const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY

function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function usePushSubscription() {
  const { user } = useAuth()
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const [row, setRow] = useState(null)        // current DB row for this device
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  // Fetch the current subscription row for this device's endpoint
  const fetchCurrent = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const reg = await navigator.serviceWorker?.ready
      const sub = await reg?.pushManager?.getSubscription?.()
      if (!sub) {
        setRow(null)
        setLoading(false)
        return
      }
      const { data, error: err } = await supabase
        .from(TABLE)
        .select('*')
        .eq('endpoint', sub.endpoint)
        .maybeSingle()
      if (err) setError(err.message)
      else setRow(data || null)
    } catch (e) {
      // SW not ready / push not supported → leave as null
      setRow(null)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchCurrent()
  }, [fetchCurrent])

  const enable = useCallback(async (hour, minute) => {
    if (!user) return false
    if (!PUBLIC_VAPID_KEY) {
      setError('VAPID public key missing — set REACT_APP_VAPID_PUBLIC_KEY')
      return false
    }
    setBusy(true)
    setError(null)
    try {
      // 1. Permission
      const perm = await Notification.requestPermission()
      setPermission(perm)
      if (perm !== 'granted') {
        setBusy(false)
        return false
      }

      // 2. Subscribe via service worker
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
        })
      }

      // 3. Persist to Supabase. The reminder_hour/minute columns on
      // push_subscriptions are deprecated (the Edge Function now reads from
      // notification_preferences instead) but we keep writing them for
      // backward compatibility until they're dropped in a cleanup PR.
      const payload = {
        user_id: user.id,
        ...subscriptionToRow(sub),
        reminder_hour: hour,
        reminder_minute: minute,
        timezone: deviceTimezone(),
        enabled: true,
        updated_at: new Date().toISOString(),
      }
      const { data, error: err } = await supabase
        .from(TABLE)
        .upsert(payload, { onConflict: 'user_id,endpoint' })
        .select()
        .maybeSingle()
      if (err) {
        setError(err.message)
        setBusy(false)
        return false
      }

      // Mirror the schedule into notification_preferences so the new
      // multi-kind scheduler picks it up. This is the authoritative
      // source going forward.
      await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          kind: 'daily_checkin',
          enabled: true,
          reminder_hour: hour,
          reminder_minute: minute,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,kind' })

      setRow(data)
      setBusy(false)
      return true
    } catch (e) {
      setError(e?.message || 'Failed to enable reminders')
      setBusy(false)
      return false
    }
  }, [user])

  const disable = useCallback(async () => {
    if (!user) return false
    setBusy(true)
    setError(null)
    try {
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) {
        await supabase.from(TABLE).delete().eq('endpoint', sub.endpoint)
        await sub.unsubscribe()
      }
      // Also turn off daily_checkin in notification_preferences. Other
      // kinds (meals/water/birthday) keep their enabled state — the
      // user can re-subscribe the device later and they'll resume.
      await supabase
        .from('notification_preferences')
        .update({ enabled: false, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('kind', 'daily_checkin')

      setRow(null)
      setBusy(false)
      return true
    } catch (e) {
      setError(e?.message || 'Failed to disable reminders')
      setBusy(false)
      return false
    }
  }, [user])

  // Diagnostic: trigger the Edge Function with test=true so the user can
  // verify the push pipeline without waiting for the scheduled time. The
  // function returns a structured result we surface as-is.
  const sendTest = useCallback(async () => {
    if (!user) return { ok: false, error: 'not signed in' }
    setBusy(true)
    setError(null)
    try {
      // Look up this device's current subscription endpoint so the server
      // can scope the test to this device only. Without this, stale
      // endpoints from prior installs also get sent to and may report
      // success without ever displaying anything on the actual device.
      const reg = await navigator.serviceWorker?.ready
      const sub = await reg?.pushManager?.getSubscription?.()
      const endpoint = sub?.endpoint || null

      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token
      if (!token) {
        setBusy(false)
        return { ok: false, error: 'no auth session' }
      }
      const { data, error: invokeErr } = await supabase.functions.invoke(
        'send-reminders',
        {
          body: { test: true, endpoint },
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      setBusy(false)
      if (invokeErr) {
        const msg = invokeErr.message || 'function call failed'
        setError(msg)
        return { ok: false, error: msg }
      }
      // Function returned 200 — but a "sent: 0" response is still a failure
      // from the user's perspective, so we surface that distinction.
      if (data && typeof data.sent === 'number') {
        if (data.sent > 0) return { ok: true, data }
        return { ok: false, error: data.message || 'no notification sent', data }
      }
      return { ok: true, data }
    } catch (e) {
      const msg = e?.message || 'failed to send test'
      setError(msg)
      setBusy(false)
      return { ok: false, error: msg }
    }
  }, [user])

  const updateTime = useCallback(async (hour, minute) => {
    if (!user || !row) return false
    setBusy(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .update({
        reminder_hour: hour,
        reminder_minute: minute,
        timezone: deviceTimezone(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', row.id)
      .select()
      .maybeSingle()
    if (err) {
      setError(err.message)
      setBusy(false)
      return false
    }
    // Mirror into notification_preferences (source of truth for the
    // scheduler).
    await supabase
      .from('notification_preferences')
      .upsert({
        user_id: user.id,
        kind: 'daily_checkin',
        enabled: true,
        reminder_hour: hour,
        reminder_minute: minute,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'user_id,kind' })

    setBusy(false)
    setRow(data)
    return true
  }, [user, row])

  return {
    permission,
    enabled: !!row?.enabled,
    reminderHour: row?.reminder_hour ?? 9,
    reminderMinute: row?.reminder_minute ?? 0,
    timezone: row?.timezone || deviceTimezone(),
    loading,
    busy,
    error,
    enable,
    disable,
    sendTest,
    updateTime,
    refetch: fetchCurrent,
    supported: typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window,
  }
}
